import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { GridColumnSortType } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { getNextColSortType } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.utils'
import { receptorNeedsCredentials } from 'src/pages/Receptors/folderScanReceptor.helpers'
import { useDocumentSourceInfo, useDocumentTree } from './DocumentPicker.hooks'
import { documentSourceSpec } from './DocumentPicker.providers'
import {
  ListPane,
  ListSortColumn,
  PathBreadcrumbs,
  ProblemBanner,
  SearchBox,
  TreePane,
  ViewToggle,
  sortNodes,
} from './DocumentPicker.components'
import {
  BrowserRoot,
  FooterActions,
  HEADER_FONT_SIZE,
  RightPane,
  TREE_PANE_WIDTH,
} from './DocumentPicker.styles'
import {
  DocumentNode,
  DocumentPickerMode,
  DocumentPickerSource,
  ROOT_PARENT_ID,
  isContainerLike,
  isLinkable,
} from './DocumentPicker.types'

export interface DocumentPickerBrowserProps {
  receptor: ReceptorRecord
  mode?: DocumentPickerMode
  source?: DocumentPickerSource
  height?: number | string
  /** Rendered under the share hint, e.g. the connection select. */
  connectionSelect?: React.ReactNode
  selectedId?: string
  /** Saved name of the initially selected folder, shown until it loads. */
  selectedFolderName?: string
  onSelectNode?: (node: DocumentNode | null) => void
  onSelectionChange?: (nodes: DocumentNode[]) => void
  onCredentialsError?: () => void
}

/**
 * Inline document browser: displays the file and directory hierarchy of a
 * connected document receptor (Google Drive, SharePoint, Confluence), with
 * tree navigation, search, and node selection.
 */
export const DocumentPickerBrowser = ({
  receptor,
  mode = 'folders',
  source: _source = 'Evidence',
  height = 520,
  connectionSelect,
  selectedId,
  selectedFolderName,
  onSelectNode,
  onSelectionChange,
  onCredentialsError,
}: DocumentPickerBrowserProps): JSX.Element => {
  const receptorId = receptor.getId()
  const modelId = receptor.getModelid() || ''
  const spec = useMemo(() => documentSourceSpec(modelId), [modelId])
  const foldersMode = mode === 'folders'

  const receptorBlocked = receptorNeedsCredentials(receptor)
  const active = !!receptorId && !receptorBlocked

  const { data: sourceInfo } = useDocumentSourceInfo(receptorId, active)
  const identity = sourceInfo?.getIdentity() || ''
  const noticeContext = useMemo(() => {
    const credentials: Record<string, string> = {}
    try {
      const parsed = JSON.parse(receptor.getCredentials() || '{}')
      Object.entries(parsed || {}).forEach(([k, v]) => {
        if (typeof v === 'string') credentials[k.toLowerCase()] = v
      })
    } catch {
      // Credentials are not always JSON; the notice then names no app id.
    }
    return { identity, credentials }
  }, [receptor, identity])

  const nodeNotice = useCallback(
    (node: DocumentNode) => spec.nodeNotice(node, noticeContext),
    [spec, noticeContext],
  )

  const serverSearch = !!sourceInfo?.getCapabilities()?.getServerSearch()
  const labelsView = !!sourceInfo?.getCapabilities()?.getLabelsView()
  const [byLabel, setByLabel] = useState(false)
  const tree = useDocumentTree(active ? receptorId : undefined, {
    includeFiles: true,
    byLabel: labelsView && byLabel,
  })
  const { state } = tree

  const [currentId, setCurrentId] = useState<string>(ROOT_PARENT_ID)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(selectedId ? [selectedId] : []),
  )
  const [currentSelected, setCurrentSelected] = useState(false)

  const prevReceptorIdRef = useRef(receptorId)
  useEffect(() => {
    if (prevReceptorIdRef.current !== receptorId) {
      prevReceptorIdRef.current = receptorId
      tree.reset()
      setByLabel(false)
      setCurrentId(ROOT_PARENT_ID)
      setExpandedIds(new Set())
      setSelectedIds(new Set(selectedId ? [selectedId] : []))
      setCurrentSelected(false)
    }
  }, [receptorId, selectedId, tree])

  const { ensureLoaded } = tree
  useEffect(() => {
    if (active) ensureLoaded(ROOT_PARENT_ID)
  }, [active, ensureLoaded])

  useEffect(() => {
    if (state.problem?.kind === 'credentials') {
      onCredentialsError?.()
    }
  }, [state.problem, onCredentialsError])

  const roots = tree.childrenOf(ROOT_PARENT_ID)
  const current =
    currentId === ROOT_PARENT_ID ? null : state.nodesById[currentId]
  const unsortedListing = state.searchResults
    ? state.searchResults.map((id) => state.nodesById[id]).filter(Boolean)
    : tree.childrenOf(currentId)

  const [sortCol, setSortCol] = useState<ListSortColumn>('name')
  const [sortType, setSortType] = useState<GridColumnSortType>(
    GridColumnSortType.ASCENDING,
  )

  const onSort = (col: ListSortColumn): void => {
    if (col === sortCol) {
      setSortType(getNextColSortType(sortType))
      return
    }
    setSortCol(col)
    setSortType(
      col === 'modified'
        ? GridColumnSortType.DESCENDING
        : GridColumnSortType.ASCENDING,
    )
  }

  const listing = useMemo(
    () => sortNodes(unsortedListing, sortCol, sortType),
    [unsortedListing, sortCol, sortType],
  )
  const listingLoading =
    !state.searchResults &&
    !state.loaded[currentId] &&
    !!state.loading[currentId]
  const path = current ? tree.pathTo(current.id) : []

  // Resolves to whether the picker moved into the node. A Confluence page
  // cannot say up front whether pages sit under it, so it is listed first
  // and a page with nothing under it is left where it is: it is a document,
  // not a place to go.
  const openNode = useCallback(
    async (node: DocumentNode): Promise<boolean> => {
      if (!isContainerLike(node)) return false
      if (node.kind === 'page' && !(await tree.ensureLoaded(node.id))) {
        return false
      }
      setCurrentId(node.id)
      setCurrentSelected(false)
      setExpandedIds((prev) => {
        const next = new Set(prev)
        tree.pathTo(node.id).forEach((n) => next.add(n.id))
        return next
      })
      tree.ensureLoaded(node.id)
      return true
    },
    [tree],
  )

  // A page already listed and found empty is not a place to open.
  const isOpenable = useCallback(
    (node: DocumentNode): boolean =>
      isContainerLike(node) &&
      !(
        node.kind === 'page' &&
        state.loaded[node.id] &&
        !tree.hasKnownChildren(node)
      ),
    [state.loaded, tree],
  )

  const jumpRoot = useCallback((): void => {
    setCurrentId(ROOT_PARENT_ID)
    setCurrentSelected(false)
  }, [])

  const toggleExpanded = useCallback(
    (node: DocumentNode): void => {
      setExpandedIds((prev) => {
        const next = new Set(prev)
        if (next.has(node.id)) next.delete(node.id)
        else {
          next.add(node.id)
          tree.ensureLoaded(node.id)
        }
        return next
      })
    },
    [tree],
  )

  const toggleSelected = useCallback(
    (node: DocumentNode): void => {
      setCurrentSelected(false)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (foldersMode) {
          if (next.has(node.id)) next.clear()
          else {
            next.clear()
            next.add(node.id)
          }
          return next
        }
        if (next.has(node.id)) next.delete(node.id)
        else next.add(node.id)
        return next
      })
    },
    [foldersMode],
  )

  const isSelectable = useCallback(
    (node: DocumentNode): boolean =>
      foldersMode
        ? isContainerLike(node) && spec.isScanRoot(node)
        : isLinkable(node),
    [foldersMode, spec],
  )

  const selectableInView = listing.filter(isSelectable)
  const selectAllInView = (): void => {
    setCurrentSelected(false)
    setSelectedIds(
      (prev) => new Set([...prev, ...selectableInView.map((n) => n.id)]),
    )
  }

  const deselectAll = (): void => {
    setCurrentSelected(false)
    setSelectedIds(new Set())
  }

  const selectCurrent = (): void => {
    if (!current) return
    setSelectedIds(new Set([current.id]))
    setCurrentSelected(true)
  }

  const selectedNodes = useMemo(
    () =>
      [...selectedIds]
        .map((id) => state.nodesById[id])
        .filter((n): n is DocumentNode => !!n),
    [selectedIds, state.nodesById],
  )

  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      if (selectedNodes.length > 0) {
        onSelectionChange?.(selectedNodes)
        onSelectNode?.(selectedNodes[0] || null)
      }
      return
    }
    onSelectionChange?.(selectedNodes)
    if (selectedIds.size === 0) {
      onSelectNode?.(null)
    } else if (selectedNodes.length > 0) {
      onSelectNode?.(selectedNodes[0])
    }
  }, [selectedNodes, selectedIds, onSelectionChange, onSelectNode])

  const { clearProblem, reload } = tree
  const problemParentId = state.problem?.parentId
  const retryProblem = useCallback((): void => {
    clearProblem()
    reload(problemParentId ?? currentId)
  }, [clearProblem, reload, problemParentId, currentId])

  const switchView = (next: 'tree' | 'labels'): void => {
    const nextByLabel = next === 'labels'
    if (nextByLabel === byLabel) return
    setByLabel(nextByLabel)
    setCurrentId(ROOT_PARENT_ID)
    setExpandedIds(new Set())
    setSelectedIds(new Set())
    setCurrentSelected(false)
  }

  const searchCandidates = useMemo<DocumentNode[]>(() => {
    if (serverSearch) return state.searchResults ? listing : []
    return Object.values(state.nodesById).filter((n) =>
      foldersMode ? isContainerLike(n) : true,
    )
  }, [serverSearch, state.searchResults, listing, state.nodesById, foldersMode])

  const onSearchPick = (node: DocumentNode): void => {
    if (isContainerLike(node)) {
      openNode(node)
      return
    }
    if (node.parentId !== ROOT_PARENT_ID && state.nodesById[node.parentId]) {
      openNode(state.nodesById[node.parentId])
    } else {
      setCurrentId(node.parentId)
      tree.ensureLoaded(node.parentId)
    }
    if (!foldersMode) setSelectedIds((prev) => new Set([...prev, node.id]))
  }

  const shareHint = identity ? spec.shareHint(identity) : ''

  const rootCaption = spec.rootCaptionFor
    ? spec.rootCaptionFor(roots)
    : spec.rootCaption

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      {labelsView && (
        <ViewToggle
          value={byLabel ? 'labels' : 'tree'}
          treeLabel={spec.rootCaption}
          onChange={switchView}
        />
      )}
      {shareHint && (
        <Typography variant="caption" color="text.secondary">
          {shareHint}
        </Typography>
      )}
      {connectionSelect}

      {state.problem && (
        <ProblemBanner
          key={`${state.problem.kind}:${state.problem.parentId ?? ''}`}
          problem={state.problem}
          vendorLabel={spec.label}
          identity={identity}
          noAccessNotice={
            state.problem.kind === 'noAccess'
              ? spec.noAccessNotice?.(
                  noticeContext,
                  (state.problem.parentId ?? ROOT_PARENT_ID) === ROOT_PARENT_ID,
                )
              : undefined
          }
          onRetry={retryProblem}
        />
      )}

      <Stack direction="row" justifyContent="flex-end">
        <Box sx={{ width: 260 }}>
          <SearchBox
            serverSearch={serverSearch}
            searching={state.searching}
            candidates={searchCandidates}
            onSearch={tree.search}
            onPick={onSearchPick}
            placeholder={
              serverSearch ? `Search ${spec.label}` : 'Search loaded items'
            }
          />
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography
          variant="caption"
          component="button"
          type="button"
          onClick={jumpRoot}
          sx={{
            width: TREE_PANE_WIDTH,
            flexShrink: 0,
            fontSize: HEADER_FONT_SIZE,
            fontWeight: 700,
            textAlign: 'left',
            border: 'none',
            background: 'none',
            p: 0,
            cursor: 'pointer',
          }}
        >
          {rootCaption}
        </Typography>
        <PathBreadcrumbs path={path} onJump={openNode} />
        <FooterActions direction="row" spacing={1} alignItems="center">
          {!foldersMode && selectedNodes.length > 0 && (
            <Chip size="small" label={`${selectedNodes.length} selected`} />
          )}
          {foldersMode && (selectedNodes.length > 0 || selectedFolderName) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              Selected: {selectedNodes[0]?.title || selectedFolderName}
            </Typography>
          )}
          {foldersMode ? (
            <Tooltip
              title={
                current && spec.isScanRoot(current)
                  ? `Use ${current.title} as the folder scan root`
                  : ''
              }
            >
              <span>
                <Button
                  size="small"
                  aria-pressed={currentSelected}
                  disabled={!current || !spec.isScanRoot(current)}
                  onClick={selectCurrent}
                >
                  Select this {current ? spec.containerNoun(current) : 'folder'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              size="small"
              onClick={selectAllInView}
              disabled={selectableInView.length === 0}
            >
              Select all
            </Button>
          )}
          <Divider orientation="vertical" flexItem />
          <Button
            size="small"
            onClick={deselectAll}
            disabled={selectedIds.size === 0}
          >
            Deselect all
          </Button>
        </FooterActions>
      </Stack>

      <BrowserRoot direction="row" spacing={2} sx={{ height }}>
        <TreePane
          roots={roots}
          notice={nodeNotice}
          childrenOf={tree.childrenOf}
          isExpandable={tree.isExpandable}
          isLoading={(id) => !!state.loading[id]}
          expandedIds={expandedIds}
          currentId={current?.id ?? null}
          onSelect={openNode}
          onToggle={toggleExpanded}
        />

        <RightPane>
          <ListPane
            nodes={listing}
            notice={nodeNotice}
            isOpenable={isOpenable}
            sort={{ col: sortCol, type: sortType, onSort }}
            loading={
              listingLoading || (state.searching && !state.searchResults)
            }
            hasMore={!state.searchResults && !!state.nextToken[currentId]}
            loadingMore={
              !!state.loading[currentId] && !!state.loaded[currentId]
            }
            onLoadMore={() => tree.loadMore(currentId)}
            selectedIds={selectedIds}
            isSelectable={isSelectable}
            onToggleSelected={toggleSelected}
            onOpen={openNode}
            emptyText={
              state.searchResults
                ? 'No matches.'
                : current
                ? `This ${spec.containerNoun(current)} is empty.`
                : 'Nothing is shared with this connection yet.'
            }
          />
        </RightPane>
      </BrowserRoot>
    </Stack>
  )
}
